import MuiLink from "@material-ui/core/Link";
import clsx from "clsx";
import type { TextColor } from "@/components/text/text";
import type { CSSProperties, PropsWithChildren } from "react";
import React, { useMemo } from "react";
import "./styles.css";
import type ColorsEnum from "../../utils/colorsEnum";
import { useTextColor } from "@/components/text/useTextColor";
import { useAnalyticsService } from "../../hooks/useAnalyticsService";
import { useHistory } from "react-router";

export type LinkType = "tel" | "mail" | "sms" | "maps" | "blank" | "tab";
export type LinkUnderLineType = "none" | "hover" | "always";

export interface LinkComponentProps {
  underline?: LinkUnderLineType;
  type?: LinkType;
  hrefUrl?: string;
  hoverColor?: TextColor;
  onClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  isStopPropagation?: boolean;
  width?: string | number;
  textColor?: ColorsEnum | TextColor;
  fontSize?: number;
  marginLeft?: number;
  fontWeight?: number;
  alignSelf?: CSSProperties["alignSelf"];
  dataCy?: string;
  className?: string;
  label?: string;
}

export function Link({
  children,
  underline = "hover",
  type,
  hrefUrl,
  hoverColor = "black",
  onClick,
  width = "auto",
  fontSize,
  textColor,
  marginLeft,
  dataCy,
  className,
  alignSelf,
  fontWeight,
  isStopPropagation = false,
  label
}: PropsWithChildren<LinkComponentProps>) {
  const { textColor: textColorValue } = useTextColor(textColor);
  const { dispatchAnalytics } = useAnalyticsService();
  const history = useHistory();
  const hrefString = useMemo(() => {
    const targetHref = hrefUrl;

    if (type === "tel") {
      return `tel:${hrefUrl}`;
    }

    if (type === "mail") {
      return `mailto:${hrefUrl}`;
    }

    if (type === "sms") {
      return `sms:${hrefUrl}`;
    }

    return targetHref;
  }, [type, hrefUrl]);

  const handleClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    if (isStopPropagation) {
      event.stopPropagation();
    }

    dispatchAnalytics("link_click", {
      link: hrefString || "no_link",
      label: label || "no_label"
    });

    event.preventDefault();

    if (
      type &&
      (type === "mail" || type === "tel" || type === "sms" || type === "maps" || type === "tab" || type === "blank")
    ) {
      onClick?.(event);
      window.open(hrefString, "_blank", "noopener,noreferrer");
    } else if (hrefString) {
      history.push(hrefString);
    }
  };

  if (onClick) {
    return (
      <span style={{ width, marginLeft, alignSelf }}>
        <MuiLink
          onClick={handleClick}
          underline={underline}
          className={clsx("DLUI_Link", className, hoverColor, textColorValue.className)}
          style={{ width, fontSize, color: textColorValue.value, fontWeight }}
          href={hrefString}
          data-cy={dataCy}
          rel={"nofollow"}
        >
          {children}
        </MuiLink>
      </span>
    );
  }

  return (
    <MuiLink
      onClick={handleClick}
      underline={underline}
      href={hrefString}
      rel={"nofollow"}
      data-cy={dataCy}
      className={clsx("DLUI_Link", className, hoverColor, textColorValue.className)}
      style={{
        width,
        color: textColorValue.value,
        marginLeft,
        fontSize,
        alignSelf,
        fontWeight
      }}
    >
      {children}
    </MuiLink>
  );
}
